import React, { useEffect, useState } from "react";
import {
    PaymentElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";
import { Alert, Button, Form } from "react-bootstrap";
import { addOrderHistory } from "../../lib/api";
import { STRIPE_REDIRECT } from "../../lib/globals";

interface StripeError {
    title: string;
    text: string;
}

export default function CheckoutForm() {
    const stripe = useStripe();
    const elements = useElements();
    const [showError, setShowError] = useState<boolean>(false);
    const [stripeError, setStripeError] = useState<StripeError>();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
        e.preventDefault();
        if (!stripe || !elements) {
            return;
        }
        setIsLoading(true);

        const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: STRIPE_REDIRECT,
            },
        });

        if (error) {
            let historyMessage = "";
            switch (error.code) {
                case "expired_card":
                    historyMessage = `Stripe payment failed due to expired card`;
                    break;
                case "incorrect_cvc":
                    historyMessage = `Stripe payment failed due to invalid card verification code (CVC)`;
                    break;
                case "incorrect_number":
                    historyMessage = `Stripe payment failed due to incorrect card number`;
                    break;
                default:
                    switch (error.decline_code) {
                        case "insufficient_funds":
                            historyMessage = `Stripe payment failed due to incorrect card number`;
                            break;
                        case "lost_card":
                            historyMessage = `Stripe payment failed due to incorrect card number`;
                            break;
                        case "stolen_card":
                            historyMessage = `Stripe payment failed due to incorrect card number`;
                            break;
                        case "fraudulent":
                            historyMessage = `Stripe payment failed due to detected fraud`;
                            break;
                        default:
                            historyMessage = `Stripe payment failed with the error: ${error?.message}`;
                    }
            }

            setStripeError({
                text: error.message as string,
                title: "An error occurred during checkout",
            });
            setShowError(true);

            if (error.payment_intent) {
                addOrderHistory(error.payment_intent?.id, historyMessage);
            }
        } else {
            setStripeError({
                text: "An unexpected error occured during processing",
                title: "If the problem persists, please contact a site admin.",
            });
            setShowError(true);
        }
        setIsLoading(false);
    };

    return (
        <Form id="paymentForm" onSubmit={handleSubmit}>
            {showError && (
                <Alert
                    dismissible
                    variant="danger"
                    onClose={() => setShowError(false)}
                    className="col-4 col-offset-4 mx-auto mt-4 mb-2 text-center stripeError"
                >
                    <Alert.Heading as="h5">{stripeError?.title}</Alert.Heading>
                    {stripeError?.text}
                </Alert>
            )}
            <PaymentElement id="payment-element" />
            <Button
                variant="primary"
                disabled={isLoading || !stripe || !elements}
                id="submit"
                type="submit"
            >
                <span id="button-text">
                    {isLoading ? "Processing" : "Pay now"}
                </span>
            </Button>
        </Form>
    );
}
